// HSA-KIT/ClientApp/src/viewer/utils/RenderUtils.jsx
// Unsorted collection of utility functions for rendering images and tiles in the viewer.

// Copyright HS Analysis GmbH, 2020
// Authors: Jakob Burghardt, Viktor Eberhardt, Lukas Buess

// External Imports
import { inv, multiply, transpose } from "mathjs";

// HSA Imports
import Backend from "../../common/utils/Backend";

let openCvPromise = null;

/**
 * Loads the OpenCV library asynchronously.
 *
 * This function checks if the OpenCV library is already loaded in the `window` object.
 * If not, it dynamically creates a script element to load the OpenCV library from the specified source.
 * It also uses a promise to handle the asynchronous loading of the library and makes sure it is only loaded once.
 *
 * @returns {Promise<void>} A promise that resolves when the OpenCV library is successfully loaded, or rejects if there is an error loading the library.
 */
export function loadOpenCv() {
  if (openCvPromise) return openCvPromise;

  openCvPromise = new Promise((resolve, reject) => {
    if (window.cv) {
      // If OpenCV is already loaded
      resolve();
    } else {
      // Dynamically create the script element
      const script = document.createElement("script");
      script.src = "opencv.js"; // Make sure the path is correct
      script.async = true;
      script.onload = function () {
        // eslint-disable-next-line no-undef
        window.cv = cv;
        resolve();
      };
      script.onerror = () => reject(new Error("Failed to load OpenCV"));
      document.body.appendChild(script);
    }
  });

  return openCvPromise;
}

// Add this cache map at the top of the file, outside any function
const imageConfigCache = new Map();

/**
 * Generates a unique cache key for an image and its channel settings.
 *
 * @param {Object} img - The image object, expected to have a `src` property.
 * @param {Object} channel - The channel settings object, containing `min`, `max`, and `gamma` properties.
 * @returns {string} A string key uniquely identifying the image and channel combination.
 */
function generateCacheKey(img, channel) {
  return `${img.src}_${channel.min}_${channel.max}_${channel.gamma}`;
}

// Helper function for gamma-only adjustment
function applyGammaOnly(img, gamma) {
  const gammaInv = 1 / gamma;
  const gammaLUT = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    gammaLUT[i] = Math.round(255 * Math.pow(i / 255, gammaInv));
  }

  const offScrCan = document.createElement("canvas");
  offScrCan.width = img.width;
  offScrCan.height = img.height;
  const ctx = offScrCan.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);

  const imgData = ctx.getImageData(0, 0, img.width, img.height);
  const data = imgData.data;

  for (let i = 0; i < data.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      data[i + j] = gammaLUT[data[i + j]];
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return offScrCan;
}

// Helper function for min/max-only adjustment
/**
 * Applies min-max normalization to the RGB channels of an image and returns a new canvas with the result.
 *
 * Each pixel's RGB value is clamped between `min` and `max`, then linearly mapped to the 0-255 range.
 * The alpha channel is left unchanged.
 *
 * @param {HTMLImageElement | HTMLCanvasElement} img - The source image or canvas to normalize.
 * @param {number} min - The minimum value for normalization (values below are clamped to 0).
 * @param {number} max - The maximum value for normalization (values above are clamped to max).
 * @returns {HTMLCanvasElement} A new canvas element containing the normalized image.
 */
function applyMinMaxOnly(img, min, max) {
  const normalizationFactor = 255 / (max - min);

  const offScrCan = document.createElement("canvas");
  offScrCan.width = img.width;
  offScrCan.height = img.height;
  const ctx = offScrCan.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);

  const imgData = ctx.getImageData(0, 0, img.width, img.height);
  const data = imgData.data;

  for (let i = 0; i < data.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      const val = Math.min(Math.max(data[i + j] - min, 0), max);
      data[i + j] = Math.round(val * normalizationFactor);
    }
  }

  ctx.putImageData(imgData, 0, 0);
  return offScrCan;
}

/**
 * Configures an image by applying min, max, and gamma adjustments based on the provided channel settings.
 *
 * - If no adjustments are needed (min=0, max=255, gamma=1.0), returns a canvas copy of the image.
 * - If only gamma adjustment is needed, applies gamma correction.
 * - If only min/max adjustment is needed, applies normalization.
 * - Otherwise, applies min, max, and gamma adjustments, with caching for performance.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} img - The source image to configure.
 * @param {{min: number, max: number, gamma: number}} channel - The channel configuration for min, max, and gamma.
 * @returns {HTMLCanvasElement|null} A canvas element with the adjusted image, or null if the input image is invalid.
 */
export function configureImage(img, channel) {
  if (!img || img.width === 0) {
    return null;
  }

  // Early exit conditions - if these values mean no change is needed
  if (channel.min === 0 && channel.max === 255 && channel.gamma === 1.0) {
    // Simply copy the image to canvas without processing
    const offScrCan = document.createElement("canvas");
    offScrCan.width = img.width;
    offScrCan.height = img.height;
    const ctx = offScrCan.getContext("2d");
    ctx.drawImage(img, 0, 0);
    return offScrCan;
  }

  // Special case: Only gamma adjustment needed
  if (channel.min === 0 && channel.max === 255 && channel.gamma !== 1.0) {
    const result = applyGammaOnly(img, channel.gamma);
    return result;
  }

  // Special case: Only min/max adjustment needed
  if (channel.gamma === 1.0) {
    const result = applyMinMaxOnly(img, channel.min, channel.max);
    return result;
  }

  // Check cache first
  const cacheKey = generateCacheKey(img, channel);
  if (imageConfigCache.has(cacheKey)) {
    const result = imageConfigCache.get(cacheKey);
    return result;
  }

  const min = channel.min;
  const max = channel.max;
  const gamma = channel.gamma;
  const normalizationFactor = 255 / (max - min);
  const gammaInv = 1 / gamma;

  // Create lookup table for gamma correction
  const gammaLUT = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    gammaLUT[i] = Math.round(255 * Math.pow(i / 255, gammaInv));
  }

  const offScrCan = document.createElement("canvas");
  offScrCan.width = img.width;
  offScrCan.height = img.height;

  const ctx1 = offScrCan.getContext("2d", { willReadFrequently: true });
  ctx1.drawImage(img, 0, 0, img.width, img.height);

  const imgData = ctx1.getImageData(0, 0, img.width, img.height);
  const data = imgData.data;

  // Process pixels in bulk using TypedArray
  for (let i = 0; i < data.length; i += 4) {
    for (let j = 0; j < 3; j++) {
      const val = Math.min(Math.max(data[i + j] - min, 0), max);
      const normalizedVal = Math.round(val * normalizationFactor);
      data[i + j] = gammaLUT[normalizedVal];
    }
  }

  ctx1.putImageData(imgData, 0, 0);

  // Store in cache before returning
  imageConfigCache.set(cacheKey, offScrCan);

  // cache size limit of 100 entries so memory does not grow indefinitely
  if (imageConfigCache.size > 100) {
    const firstKey = imageConfigCache.keys().next().value;
    imageConfigCache.delete(firstKey);
  }
  return offScrCan;
}

var __assign =
  (this && this.__assign) ||
  function () {
    __assign =
      Object.assign ||
      function (t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
          s = arguments[i];
          for (var p in s)
            if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
      };
    return __assign.apply(this, arguments);
  };
//function is necessary for procrustes calculation
function SVD(a, options) {
  let _a = __assign({ u: true, v: true, eps: Math.pow(2, -52) }, options),
    withu = _a.u,
    withv = _a.v,
    eps = _a.eps;
  const tol = 1e-64 / eps;
  // throw error if a is not defined
  if (!a) {
    throw new TypeError("Matrix a is not defined");
  }
  // Householder's reduction to bidiagonal form
  var n = a[0].length;
  var m = a.length;
  if (m < n) {
    throw new TypeError("Invalid matrix: m < n");
  }
  var l1, c, f, h, s, y, z;
  var l = 0,
    g = 0,
    x = 0;
  var e = [];
  var u = [];
  var v = [];
  // Initialize u
  for (let i = 0; i < m; i++) {
    u[i] = new Array(n).fill(0);
  }
  // Initialize v
  for (let i = 0; i < n; i++) {
    v[i] = new Array(n).fill(0);
  }
  // Initialize q
  var q = new Array(n).fill(0);
  // Copy array a in u
  for (let i = 0; i < m; i++) {
    for (let j = 0; j < n; j++) {
      u[i][j] = a[i][j];
    }
  }
  for (let i = 0; i < n; i++) {
    e[i] = g;
    s = 0;
    l = i + 1;
    for (let j = i; j < m; j++) {
      s += Math.pow(u[j][i], 2);
    }
    if (s < tol) {
      g = 0;
    } else {
      f = u[i][i];
      g = f < 0 ? Math.sqrt(s) : -Math.sqrt(s);
      h = f * g - s;
      u[i][i] = f - g;
      for (let j = l; j < n; j++) {
        s = 0;
        for (let k = i; k < m; k++) {
          s += u[k][i] * u[k][j];
        }
        f = s / h;
        for (let k = i; k < m; k++) {
          u[k][j] = u[k][j] + f * u[k][i];
        }
      }
    }
    q[i] = g;
    s = 0;
    for (let j = l; j < n; j++) {
      s += Math.pow(u[i][j], 2);
    }
    if (s < tol) {
      g = 0;
    } else {
      f = u[i][i + 1];
      g = f < 0 ? Math.sqrt(s) : -Math.sqrt(s);
      h = f * g - s;
      u[i][i + 1] = f - g;
      for (let j = l; j < n; j++) {
        e[j] = u[i][j] / h;
      }
      for (let j = l; j < m; j++) {
        s = 0;
        for (let k = l; k < n; k++) {
          s += u[j][k] * u[i][k];
        }
        for (let k = l; k < n; k++) {
          u[j][k] = u[j][k] + s * e[k];
        }
      }
    }
    y = Math.abs(q[i]) + Math.abs(e[i]);
    if (y > x) {
      x = y;
    }
  }
  // Accumulation of right-hand transformations
  if (withv) {
    for (let i = n - 1; i >= 0; i--) {
      if (g !== 0) {
        h = u[i][i + 1] * g;
        for (let j = l; j < n; j++) {
          v[j][i] = u[i][j] / h;
        }
        for (let j = l; j < n; j++) {
          s = 0;
          for (let k = l; k < n; k++) {
            s += u[i][k] * v[k][j];
          }
          for (let k = l; k < n; k++) {
            v[k][j] = v[k][j] + s * v[k][i];
          }
        }
      }
      for (let j = l; j < n; j++) {
        v[i][j] = 0;
        v[j][i] = 0;
      }
      v[i][i] = 1;
      g = e[i];
      l = i;
    }
  }
  // Accumulation of left-hand transformations
  if (withu) {
    for (let i = n - 1; i >= 0; i--) {
      l = i + 1;
      g = q[i];
      for (let j = l; j < n; j++) {
        u[i][j] = 0;
      }
      if (g !== 0) {
        h = u[i][i] * g;
        for (let j = l; j < n; j++) {
          s = 0;
          for (let k = l; k < m; k++) {
            s += u[k][i] * u[k][j];
          }
          f = s / h;
          for (let k = i; k < m; k++) {
            u[k][j] = u[k][j] + f * u[k][i];
          }
        }
        for (let j = i; j < m; j++) {
          u[j][i] = u[j][i] / g;
        }
      } else {
        for (let j = i; j < m; j++) {
          u[j][i] = 0;
        }
      }
      u[i][i] = u[i][i] + 1;
    }
  }
  // Diagonalization of the bidiagonal form
  eps = eps * x;
  var testConvergence;
  for (let k = n - 1; k >= 0; k--) {
    for (let iteration = 0; iteration < 50; iteration++) {
      // test-f-splitting
      testConvergence = false;
      for (l = k; l >= 0; l--) {
        if (Math.abs(e[l]) <= eps) {
          testConvergence = true;
          break;
        }
        if (Math.abs(q[l - 1]) <= eps) {
          break;
        }
      }
      if (!testConvergence) {
        // cancellation of e[l] if l>0
        c = 0;
        s = 1;
        l1 = l - 1;
        for (let i = l; i < k + 1; i++) {
          f = s * e[i];
          e[i] = c * e[i];
          if (Math.abs(f) <= eps) {
            break; // goto test-f-convergence
          }
          g = q[i];
          q[i] = Math.sqrt(f * f + g * g);
          h = q[i];
          c = g / h;
          s = -f / h;
          if (withu) {
            for (let j = 0; j < m; j++) {
              y = u[j][l1];
              z = u[j][i];
              u[j][l1] = y * c + z * s;
              u[j][i] = -y * s + z * c;
            }
          }
        }
      }
      // test f convergence
      z = q[k];
      if (l === k) {
        // convergence
        if (z < 0) {
          // q[k] is made non-negative
          q[k] = -z;
          if (withv) {
            for (var j = 0; j < n; j++) {
              v[j][k] = -v[j][k];
            }
          }
        }
        break; // break out of iteration loop and move on to next k value
      }
      // Shift from bottom 2x2 minor
      x = q[l];
      y = q[k - 1];
      g = e[k - 1];
      h = e[k];
      f = ((y - z) * (y + z) + (g - h) * (g + h)) / (2 * h * y);
      g = Math.sqrt(f * f + 1);
      f = ((x - z) * (x + z) + h * (y / (f < 0 ? f - g : f + g) - h)) / x;
      // Next QR transformation
      c = 1;
      s = 1;
      for (let i = l + 1; i < k + 1; i++) {
        g = e[i];
        y = q[i];
        h = s * g;
        g = c * g;
        z = Math.sqrt(f * f + h * h);
        e[i - 1] = z;
        c = f / z;
        s = h / z;
        f = x * c + g * s;
        g = -x * s + g * c;
        h = y * s;
        y = y * c;
        if (withv) {
          for (let j = 0; j < n; j++) {
            x = v[j][i - 1];
            z = v[j][i];
            v[j][i - 1] = x * c + z * s;
            v[j][i] = -x * s + z * c;
          }
        }
        z = Math.sqrt(f * f + h * h);
        q[i - 1] = z;
        c = f / z;
        s = h / z;
        f = c * g + s * y;
        x = -s * g + c * y;
        if (withu) {
          for (let j = 0; j < m; j++) {
            y = u[j][i - 1];
            z = u[j][i];
            u[j][i - 1] = y * c + z * s;
            u[j][i] = -y * s + z * c;
          }
        }
      }
      e[l] = 0;
      e[k] = f;
      q[k] = x;
    }
  }
  // Number below eps should be zero
  for (let i = 0; i < n; i++) {
    if (q[i] < eps) q[i] = 0;
  }
  return { u: u, q: q, v: v };
}

function calculateAffineTransformation(opointBase, opointShift, bfact, tfact) {
  //copy arrays
  const pointBase = JSON.parse(JSON.stringify(opointBase));
  const pointShift = JSON.parse(JSON.stringify(opointShift));

  for (let i = 0; i < pointBase.length; i++) {
    pointShift[i][0] = pointShift[i][0] * tfact;
    pointShift[i][1] = pointShift[i][1] * tfact;
    pointBase[i][0] = pointBase[i][0] * bfact;
    pointBase[i][1] = pointBase[i][1] * bfact;
  }

  //procrustes Calculation
  const n = pointBase.length;
  const ny = pointShift.length;

  let muXSumX = 0;
  let muXSumY = 0;
  for (let i = 0; i < n; i++) {
    muXSumX += pointBase[i][0];
    muXSumY += pointBase[i][1];
  }
  let muYSumX = 0;
  let muYSumY = 0;
  for (let i = 0; i < ny; i++) {
    muYSumX += pointShift[i][0];
    muYSumY += pointShift[i][1];
  }
  const muX = [muXSumX / n, muXSumY / n];
  const muY = [muYSumX / ny, muYSumY / ny];

  const x0 = [];
  const y0 = [];
  for (let i = 0; i < n; i++) {
    x0.push([pointBase[i][0] - muX[0], pointBase[i][1] - muX[1]]);
  }
  for (let i = 0; i < ny; i++) {
    y0.push([pointShift[i][0] - muY[0], pointShift[i][1] - muY[1]]);
  }

  let muXSumXQ = 0;
  let muXSumYQ = 0;
  for (let i = 0; i < n; i++) {
    muXSumXQ += x0[i][0] ** 2;
    muXSumYQ += x0[i][1] ** 2;
  }
  let muYSumXQ = 0;
  let muYSumYQ = 0;
  for (let i = 0; i < ny; i++) {
    muYSumXQ += y0[i][0] ** 2;
    muYSumYQ += y0[i][1] ** 2;
  }
  const ssX = muXSumXQ + muXSumYQ;
  const ssY = muYSumXQ + muYSumYQ;

  const normX = Math.sqrt(ssX);
  const normY = Math.sqrt(ssY);

  const newX0 = [];
  const newY0 = [];
  for (let i = 0; i < n; i++) {
    newX0.push([x0[i][0] / normX, x0[i][1] / normX]);
  }
  for (let i = 0; i < ny; i++) {
    newY0.push([y0[i][0] / normY, y0[i][1] / normY]);
  }

  const a = multiply(transpose(newX0), newY0);
  const { u, q, v } = SVD(a);

  const U = JSON.parse(JSON.stringify(u));
  U[0][0] = u[0][1];
  U[0][1] = u[0][0];
  U[1][0] = u[1][1];
  U[1][1] = u[1][0];
  const Vt = JSON.parse(JSON.stringify(v));
  Vt[0][0] = v[0][1];
  Vt[0][1] = v[0][0];
  Vt[1][0] = v[1][1];
  Vt[1][1] = v[1][0];
  const s = JSON.parse(JSON.stringify(q));
  s[0] = q[1];
  s[1] = q[0];

  const V = transpose(Vt);

  const T = multiply(V, transpose(U));
  // reflection always at best
  // scaling always true
  // view python code for details
  let traceTA = 0;
  for (let i = 0; i < s.length; i++) {
    traceTA += s[i];
  }
  const b = (traceTA * normX) / normY;
  const Z = multiply(newY0, T);

  for (let i = 0; i < Z.length; i++) {
    Z[i][0] = normX * traceTA * Z[i][0] + muX[0];
    Z[i][1] = normX * traceTA * Z[i][1] + muX[1];
  }
  let c = multiply(muY, T);
  c[0] = muX[0] - c[0] * b;
  c[1] = muX[1] - c[1] * b;
  const R = [
    [T[0][0], T[0][1], 0],
    [T[1][0], T[1][1], 0],
    [0, 0, 1],
  ];
  const S = [
    [b, 0, 0],
    [0, b, 0],
    [0, 0, 1],
  ];
  const t = [
    [1, 0, c[0]],
    [0, 1, c[1]],
    [0, 0, 1],
  ];
  const Matrix = transpose(multiply(multiply(R, S), transpose(t)));
  return Matrix;
}

function calcOnloading(params, img) {
  img.onload = () => {
    params.counter += 1;
    params.imgs.push(img);
    if (params.counter === 2) {
      let str = params.imgs[0].currentSrc;
      //let baseHeight = null;
      let targetHeight = null;
      if (str.includes(params.baseId)) {
        //baseHeight = params.imgs[0].height;
        targetHeight = params.imgs[1].height;
      } else {
        return;
      }

      let bfact = 1; // baseHeight / params.baseOme.sizeY;
      let tfact = 1;
      let translateScale = targetHeight / params.ome.sizeY; //baseHeight / params.baseOme.sizeY;

      let m = calculateAffineTransformation(
        params.pointBase,
        params.pointShift,
        bfact,
        tfact
      );
      let hMat = [
        m[0][0],
        m[0][1],
        m[0][2],
        m[1][0],
        m[1][1],
        m[1][2],
        m[2][0],
        m[2][1],
        m[2][2],
      ];
      params.callback(hMat, translateScale);
    }
  };
}

export function calculateTransformationMatrix(
  pointBase,
  pointShift,
  ome,
  baseId,
  targetId,
  baseOme,
  callback
) {
  let ids = [baseId, targetId];
  let counter = 0;
  let imgs = [];
  let params = {
    counter,
    imgs,
    pointBase,
    pointShift,
    ome,
    baseOme,
    callback,
    baseId,
  };
  for (let i = 0; i < ids.length; i++) {
    let img = new Image();
    img.src = Backend.renderRegion({
      id: ids[i],
      page: 0,
      lv: 0,
      x: 0,
      y: 0,
    });
    calcOnloading(params, img);
  }
}

function onloading(p, i, j, visImg) {
  visImg.onload = () => {
    p.counter += 1;
    p.imgs.push(visImg);
    p.pos.push([j - p.xStart, i - p.yStart]);
    if (p.counter === p.tilesToLoad) {
      p.callback(p.imgs, p.pos);
    }
  };
}

function tileLoader(params) {
  for (let i = params.yStart; i <= params.yEnd; i++) {
    for (let j = params.xStart; j <= params.xEnd; j++) {
      let visImg = new Image();
      visImg.src = Backend.renderRegion({
        id: params.fileId,
        page: params.page,
        lv: params.lv,
        x: j,
        y: i,
      });
      onloading(params, i, j, visImg);
    }
  }
}

function transformPoint(x, y, m) {
  const a = m[0][0] * x + m[0][1] * y + m[0][2];
  const b = m[1][0] * x + m[1][1] * y + m[1][2];
  const c = m[2][0] * x + m[2][1] * y + m[2][2];
  if (c !== 1) {
    console.log("transformation error");
  }
  const resultX = a / c;
  const resultY = b / c;
  return [resultX, resultY];
}

export function tileRegistration(
  img,
  tileId,
  hMat,
  fact,
  channel,
  callbackPushImage
) {
  const cv = window.cv;
  const splited = tileId.split(",");
  const page = parseInt(splited[0]);
  const lv = parseInt(splited[1]);
  const x = parseInt(splited[2]);
  const y = parseInt(splited[3]);
  const fileId = splited[4];
  const maxTile = Math.pow(2, lv);
  let m = [
    [hMat[0], hMat[1], hMat[2] * fact],
    [hMat[3], hMat[4], hMat[5] * fact],
    [hMat[6], hMat[7], hMat[8]],
  ];

  let iM = inv(m);
  const s = [
    [maxTile, 0, 0],
    [0, maxTile, 0],
    [0, 0, 1],
  ];
  m = multiply(multiply(s, m), inv(s));
  iM = inv(m);

  //get tile coordinates
  const tileWidth = img.width;
  const tileHeight = img.height;
  const xPosition = (x + 1) * tileWidth;
  const yPosition = (y + 1) * tileHeight;

  const pointTL = [xPosition - tileWidth, yPosition - tileHeight];
  const pointTR = [xPosition, yPosition - tileHeight];
  const pointBL = [xPosition - tileWidth, yPosition];
  const pointBR = [xPosition, yPosition];

  //reverse transfrom the tile coordinates
  const invPointTL = transformPoint(pointTL[0], pointTL[1], iM);
  const invPointTR = transformPoint(pointTR[0], pointTR[1], iM);
  const invPointBL = transformPoint(pointBL[0], pointBL[1], iM);
  const invPointBR = transformPoint(pointBR[0], pointBR[1], iM);

  //create rectangle around transformed coordinates
  const minX = Math.min(
    invPointTL[0],
    invPointTR[0],
    invPointBL[0],
    invPointBR[0]
  );
  const minY = Math.min(
    invPointTL[1],
    invPointTR[1],
    invPointBL[1],
    invPointBR[1]
  );
  const maxX = Math.max(
    invPointTL[0],
    invPointTR[0],
    invPointBL[0],
    invPointBR[0]
  );
  const maxY = Math.max(
    invPointTL[1],
    invPointTR[1],
    invPointBL[1],
    invPointBR[1]
  );

  const rectPointTL = [minX, minY];
  const rectPointBR = [maxX, maxY];
  const rectWidth = Math.abs(maxX - minX);
  const rectHeight = Math.abs(maxY - minY);

  //create mats and use them to calc needed transformation matrix
  const OriginRectPointTL = [
    invPointTL[0] - rectPointTL[0],
    invPointTL[1] - rectPointTL[1],
  ];
  const OriginRectPointTR = [
    invPointTR[0] - rectPointTL[0],
    invPointTR[1] - rectPointTL[1],
  ];
  const OriginRectPointBL = [
    invPointBL[0] - rectPointTL[0],
    invPointBL[1] - rectPointTL[1],
  ];
  const OriginRectPointBR = [
    invPointBR[0] - rectPointTL[0],
    invPointBR[1] - rectPointTL[1],
  ];

  let matSource = [
    OriginRectPointTL[0],
    OriginRectPointTL[1],
    OriginRectPointTR[0],
    OriginRectPointTR[1],
    OriginRectPointBL[0],
    OriginRectPointBL[1],
    OriginRectPointBR[0],
    OriginRectPointBR[1],
  ];

  matSource = cv.matFromArray(4, 1, cv.CV_32FC2, matSource);
  let matTarget = [0, 0, tileWidth, 0, 0, tileHeight, tileWidth, tileHeight];
  matTarget = cv.matFromArray(4, 1, cv.CV_32FC2, matTarget);
  let newMat = cv.getPerspectiveTransform(matSource, matTarget);

  //calculate wich tiles need to be loaded for complete information
  let xStart = x;
  let yStart = y;
  let xEnd = x;
  let yEnd = y;
  if (rectPointTL[0] < pointTL[0] && pointTL[0] !== 0) {
    let diff = pointTL[0] - rectPointTL[0];
    diff = diff / tileWidth;
    xStart = xStart - (Math.trunc(diff) + 1);
    if (xStart < 0) xStart = 0;
  }
  if (rectPointBR[0] > pointBR[0]) {
    let diff = rectPointBR[0] - pointBR[0];
    diff = diff / tileWidth;
    xEnd = xEnd + (Math.trunc(diff) + 1);
    if (xEnd > maxTile - 1) xEnd = maxTile - 1;
  }
  if (rectPointTL[1] < pointTL[1] && pointTL[1] !== 0) {
    let diff = pointTL[1] - rectPointTL[1];
    diff = diff / tileHeight;
    yStart = yStart - (Math.trunc(diff) + 1);
    if (yStart < 0) yStart = 0;
  }
  if (rectPointBR[1] > pointBR[1]) {
    let diff = rectPointBR[1] - pointBR[1];
    diff = diff / tileHeight;
    yEnd = yEnd + (Math.trunc(diff) + 1);
    if (yEnd > maxTile - 1) yEnd = maxTile - 1;
  }

  //create offline canvas
  const offScrCan = document.createElement("canvas");
  offScrCan.width = (xEnd - xStart + 1) * tileWidth;
  offScrCan.height = (yEnd - yStart + 1) * tileHeight;
  let offsetX = 0;
  let offsetY = 0;

  //adjust canvas size
  if (rectPointTL[0] - pointTL[0] < 0) {
    offScrCan.width = offScrCan.width + Math.abs(rectPointTL[0] - pointTL[0]);
    offsetX = Math.abs(rectPointTL[0] - pointTL[0]);
  }
  if (rectPointBR[0] - pointBR[0] > (xEnd - xStart + 1) * tileWidth) {
    offScrCan.width = offScrCan.width + Math.abs(rectPointBR[0] - pointBR[0]);
  }
  if (rectPointTL[1] - pointTL[1] < 0) {
    offScrCan.height = offScrCan.height + Math.abs(rectPointTL[1] - pointTL[1]);
    offsetY = Math.abs(rectPointTL[1] - pointTL[1]);
  }
  if (rectPointBR[1] - pointBR[1] > (yEnd - yStart + 1) * tileHeight) {
    offScrCan.height = offScrCan.height + Math.abs(rectPointBR[1] - pointBR[1]);
  }

  const ctx = offScrCan.getContext("2d");
  //load the tiles wich will be later drawn into offline canvas
  const imgs = [];
  const pos = [];
  const counter = 0;
  const tilesToLoad = (yEnd - yStart + 1) * (xEnd - xStart + 1);
  //tileloaderparams
  let tileLoaderParams = {
    yStart,
    yEnd,
    xStart,
    xEnd,
    fileId,
    page,
    lv,
    tilesToLoad,
    counter,
    imgs,
    pos,
    callback: (imgs, pos) => {
      if (imgs.length === pos.length) {
        for (let i = 0; i < imgs.length; i++) {
          const p = pos[i];
          ctx.drawImage(
            imgs[i],
            p[0] * tileWidth + offsetX,
            p[1] * tileHeight + offsetY,
            tileWidth,
            tileHeight
          );
        }
      } else {
        console.log("RETURN ERROR");
        return;
      }
      //create rectangle with information from offline canvas
      const imgData = ctx.getImageData(
        rectPointTL[0] - xStart * tileWidth + offsetX,
        rectPointTL[1] - yStart * tileHeight + offsetY,
        rectWidth,
        rectHeight
      );
      //transform the rectangle
      const src = cv.matFromImageData(imgData);
      const dst = new cv.Mat();
      const dsize = new cv.Size(tileWidth, tileHeight);
      cv.warpPerspective(
        src,
        dst,
        newMat,
        dsize,
        cv.INTER_NEAREST,
        cv.BORDER_REPLICATE
      );

      const newImgData = new ImageData(
        new Uint8ClampedArray(dst.data),
        dst.cols,
        dst.rows
      );
      //create new canvas in tile size and draw transformation result into it
      let offTileCan = document.createElement("canvas");
      offTileCan.width = tileWidth;
      offTileCan.height = tileHeight;
      let ctxTile = offTileCan.getContext("2d");
      ctxTile.putImageData(newImgData, 0, 0);
      offTileCan = configureImage(offTileCan, channel);

      callbackPushImage(offTileCan);
    },
  };
  tileLoader(tileLoaderParams);
}

export function colorInImage(img, channel) {
  if (!img || img.width === 0) {
    return null;
  }

  const offScrCan = document.createElement("canvas");
  offScrCan.width = img.width;
  offScrCan.height = img.height;

  const ctx1 = offScrCan.getContext("2d");

  ctx1.drawImage(img, 0, 0, img.width, img.height);

  // do histogram manipulation
  if (img.width === 0) return null;
  const imgData = ctx1.getImageData(0, 0, img.width, img.height);
  const step = 4; // rgba
  const min = channel.min;
  const max = channel.max;
  const gamma = channel.gamma;
  const maxPixelValue = 255;
  const rgb = false;

  //let rgb = true;
  for (let i = 0, n = imgData.data.length; i < n; i += step) {
    let val = imgData.data[i];
    // let val = imgData.data[i + (step - 1)];
    // if (val < maxPixelValue) rgb = false;

    val = (Math.min(Math.max(val - min, 0), max) * maxPixelValue) / (max - min);
    imgData.data[i + (step - 1)] =
      maxPixelValue * (val / maxPixelValue) ** (1 / gamma);
  }

  ctx1.putImageData(imgData, 0, 0);

  if (channel.type !== "brightfield" && !rgb) {
    // color in alpha channel
    ctx1.globalCompositeOperation = "source-in";
    ctx1.fillStyle = channel.color;
    ctx1.fillRect(0, 0, img.width, img.height);
  }
  return offScrCan;
}
